import type {Ability} from '../../../../generated/types';
import {ABILITIES, DEFAULT_ABILITY_SCORE} from './abilities';
import type {AbilityGeneration} from './ability-generation';
import type {StartingSelection, StartingSource} from './starting-equipment';

export const MAX_CHARACTER_LEVEL = 20;

export type ClassLevel = {
	classId: string;
	level: number;
};

export type HitPointsState = {
	/** потраченные пз */
	spent: number;
	/** полученные временные пз */
	temporary: number;
	/** потраченные временные пз */
	temporarySpent: number;
};

export type DeathSaves = {
	successes: number;
	failures: number;
};

export const COINS = ['cp', 'sp', 'ep', 'gp', 'pp'] as const;
export type Coin = (typeof COINS)[number];

export type EquipmentEntry = {
	/** ключ записи: один и тот же предмет может лежать несколькими записями */
	key: string;
	itemId: string;
	quantity: number;
	/** предмет используется: оружие в руках, надетый доспех или щит */
	equipped?: boolean;
	/** оружие во второй руке: даёт атаку бонусным действием */
	offHand?: boolean;
	/** выборы по choose предмета: ключ группы → выбранные значения; `baseItem` — основа для «Броня +1» */
	choices?: Record<string, string[]>;
	/** предмет добавлен стартовым снаряжением: при смене варианта он убирается */
	startingSource?: StartingSource;
};

export const NOTE_SECTIONS = ['organizations', 'allies', 'enemies', 'backstory', 'other'] as const;
export type NoteSection = (typeof NOTE_SECTIONS)[number];

export type JournalEntryKind = 'roll' | 'rest' | 'hitPoints' | 'spell' | 'feature' | 'item' | 'experience';

export type JournalEntry = {
	id: string;
	/** время записи в миллисекундах */
	time: number;
	kind: JournalEntryKind;
	title: string;
	/** подробности: кубы и слагаемые броска, итог отдыха */
	details?: string;
	/** итог броска */
	total?: number;
};

export const SPEED_KINDS = ['walk', 'fly', 'swim', 'climb', 'burrow'] as const;
export type SpeedKind = (typeof SPEED_KINDS)[number];

export const MAX_EXHAUSTION = 6;

export type SheetOverrides = {
	/** прибавка к максимуму ПЗ, держится до долгого отдыха */
	maxHitPointsBonus?: number;
	/** максимум ПЗ вместо расчётного, держится до долгого отдыха */
	maxHitPoints?: number;
	speeds?: Partial<Record<SpeedKind, number>>;
};

/** значения выборов: ключ экземпляра группы → выбранные ключи вариантов */
export type ChoiceValues = Record<string, string[]>;

export type CharacterState = {
	name: string;
	/** классы по порядку взятия: первый — стартовый */
	classes: ClassLevel[];
	/** значения характеристик до повышений от происхождения, черт и особенностей */
	baseAbilities: Record<Ability, number>;
	/** способ набора характеристик в мастере и незаконченные назначения */
	abilityGeneration?: AbilityGeneration;
	specieId?: string;
	/** выбранный размер, если вид позволяет выбрать */
	size?: string;
	originId?: string;
	/** мировоззрение: LG, NG, CG, LN, N, CN, LE, NE, CE */
	alignment?: string;
	choices: ChoiceValues;
	/** заклинания, вписанные в книгу помимо выборов класса: переписанные со свитков и из чужих книг; класс → заклинания */
	spellbookSpells?: Record<string, string[]>;
	hitPoints: HitPointsState;
	/** потраченные ячейки: круг или `pact` для магии договора → количество */
	spentSpellSlots: Record<string, number>;
	/** потраченные использования: ключ ресурса, действия или заклинания → количество */
	spentUses: Record<string, number>;
	/** потраченные кости здоровья: размер кости → количество */
	spentHitDice: Record<string, number>;
	/** выпавшие при повышении уровня кости здоровья: `${classId}:${уровень класса}` → значение кости */
	hitPointRolls: Record<string, number>;
	deathSaves: DeathSaves;
	heroicInspiration: boolean;
	/** состояния ConditionType, кроме утомления */
	conditions: string[];
	/** степень утомления от 0 до 6 */
	exhaustion: number;
	overrides: SheetOverrides;
	/** заклинания, которые сейчас действуют на персонажа: их props попадают в лист */
	activeSpells: ActiveSpell[];
	coins: Record<Coin, number>;
	equipment: EquipmentEntry[];
	/** ключи записей снаряжения, к которым персонаж настроен */
	attunement: string[];
	/** вещи, которых нет в базе: произвольные строки */
	otherItems: string[];
	/** выбранные варианты стартового снаряжения класса и предыстории */
	startingEquipment?: Partial<Record<StartingSource, StartingSelection>>;
	/** очки опыта */
	experience?: number;
	notes: Record<NoteSection, string[]>;
	/** журнал бросков и отдыхов, новые записи в конце */
	journal: JournalEntry[];
};

export type ActiveSpell = {
	spellId: string;
	/** выборы по choose заклинания: вид урона, характеристика */
	choices?: Record<string, string[]>;
	/** заклинательная характеристика того, кто наложил: для формул `{spellcasting}` */
	spellcastingModifier?: number;
};

export type CharacterStatePatch = Partial<CharacterState> | ((state: CharacterState) => Partial<CharacterState>);

export const createCharacterState = (): CharacterState => ({
	name: '',
	classes: [],
	baseAbilities: Object.fromEntries(ABILITIES.map((ability) => [ability, DEFAULT_ABILITY_SCORE])) as Record<
		Ability,
		number
	>,
	abilityGeneration: {method: 'standard', assigned: {}},
	choices: {},
	hitPoints: {spent: 0, temporary: 0, temporarySpent: 0},
	spentSpellSlots: {},
	spentUses: {},
	spentHitDice: {},
	hitPointRolls: {},
	deathSaves: {successes: 0, failures: 0},
	heroicInspiration: false,
	conditions: [],
	exhaustion: 0,
	overrides: {},
	activeSpells: [],
	coins: {cp: 0, sp: 0, ep: 0, gp: 0, pp: 0},
	equipment: [],
	attunement: [],
	otherItems: [],
	notes: {organizations: [], allies: [], enemies: [], backstory: [], other: []},
	journal: [],
});

export const characterLevel = (classes: ClassLevel[]) =>
	Math.max(
		1,
		classes.reduce((total, classLevel) => total + classLevel.level, 0),
	);
